import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { customFetcher } from '../useFetch';

/** Verwaltung von Postleitzahlen (Admin) — serverseitige Suche/Pagination,
 * CRUD sowie transaktionale Migration/Löschung. */
export interface IPostcode {
  idPostcode: number;
  Postcode: string;
  City: string;
  City_idCity?: number | null;
  FederalState_idFederalState?: number | null;
  FederalStateName?: string | null;
  Country_idCountry?: number | null;
}

export interface IPostcodeListResult {
  rows: IPostcode[];
  total: number;
  page: number;
  rowsPerPage: number;
}

export interface IPostcodeReference {
  table: string;
  column: string;
  count: number;
}

export interface IPostcodeReferences {
  byTable: IPostcodeReference[];
  total: number;
}

export const usePostcodeList = (
  search: string,
  page: number,
  rowsPerPage: number,
  searchType = 'includes'
) => {
  const { data, isFetching, refetch } = useQuery<IPostcodeListResult>({
    queryKey: ['postcodeList', search, searchType, page, rowsPerPage],
    queryFn: () =>
      customFetcher({
        endpoint: `/postcode/admin/list?search=${encodeURIComponent(
          search
        )}&searchtype=${searchType}&page=${page}&rowsPerPage=${rowsPerPage}`,
      }),
    placeholderData: keepPreviousData,
  });
  return { postcodeResult: data, isFetchingPostcodes: isFetching, refetch };
};

export const useCreatePostcode = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createPostcode, isPending: isCreatingPostcode } =
    useMutation({
      mutationFn: (body: Partial<IPostcode>) =>
        customFetcher({
          endpoint: '/postcode/admin/list',
          method: 'POST',
          body,
        }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['postcodeList'] }),
    });
  return { createPostcode, isCreatingPostcode };
};

export const useUpdatePostcode = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updatePostcode, isPending: isUpdatingPostcode } =
    useMutation({
      mutationFn: ({
        idPostcode,
        body,
      }: {
        idPostcode: number;
        body: Partial<IPostcode>;
      }) =>
        customFetcher({
          endpoint: `/postcode/admin/${idPostcode}`,
          method: 'PUT',
          body,
        }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['postcodeList'] }),
    });
  return { updatePostcode, isUpdatingPostcode };
};

export const usePostcodeReferences = () => {
  const { mutateAsync: loadReferences, isPending: isLoadingReferences } =
    useMutation({
      mutationFn: (idPostcodes: number[]) =>
        customFetcher({
          endpoint: '/postcode/admin/references',
          method: 'POST',
          body: { idPostcodes },
        }) as Promise<IPostcodeReferences>,
    });
  return { loadReferences, isLoadingReferences };
};

export const useMigratePostcodes = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: migratePostcodes, isPending: isMigratingPostcodes } =
    useMutation({
      mutationFn: ({
        sourceIds,
        targetId,
      }: {
        sourceIds: number[];
        targetId: number;
      }) =>
        customFetcher({
          endpoint: '/postcode/admin/migrate',
          method: 'POST',
          body: { sourceIds, targetId },
        }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['postcodeList'] }),
    });
  return { migratePostcodes, isMigratingPostcodes };
};

export const useDeletePostcodes = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: deletePostcodes, isPending: isDeletingPostcodes } =
    useMutation({
      mutationFn: ({
        sourceIds,
        targetId,
      }: {
        sourceIds: number[];
        targetId: number;
      }) =>
        customFetcher({
          endpoint: '/postcode/admin/delete',
          method: 'POST',
          body: { sourceIds, targetId },
        }),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['postcodeList'] }),
    });
  return { deletePostcodes, isDeletingPostcodes };
};
